import * as Sentry from '@sentry/react'
import { useEffect, useMemo, useRef } from 'react'
import { useMatches } from 'react-router'

type SentryRouteHandle = { sentryTransactionPath?: string }

function useRouteTransactionPath(): string | undefined {
  const matches = useMatches()
  return useMemo(() => {
    for (let i = matches.length - 1; i >= 0; i--) {
      const path = (matches[i]!.handle as SentryRouteHandle | undefined)
        ?.sentryTransactionPath
      if (path) return path
    }
    return undefined
  }, [matches])
}

/**
 * Starts a Sentry transaction for the open panel so it shows in Performance.
 * Call with panel name when open (e.g. "summary", "alert") or null when closed.
 *
 * The base transaction name is resolved from (in priority order):
 * 1. The active root span description (set by Sentry's React Router integration)
 * 2. The matched route's handle.sentryTransactionPath (set per-feature in Routes.tsx)
 * 3. 'unknown'
 */
export const usePanelSentryTransaction = (panelName: string | null): void => {
  const routeTransactionPath = useRouteTransactionPath()
  const spanRef = useRef<ReturnType<typeof Sentry.startInactiveSpan> | null>(
    null,
  )
  const originalTxNameRef = useRef<string | undefined>(undefined)
  const previousActiveSpanRef = useRef<ReturnType<
    typeof Sentry.startInactiveSpan
  > | null>(null)

  useEffect(() => {
    if (!import.meta.env.VITE_SENTRY_DSN) return

    if (!panelName) return

    if (spanRef.current) spanRef.current.end()

    const activeSpan = Sentry.getActiveSpan()
    const rootSpan = activeSpan ? Sentry.getRootSpan(activeSpan) : undefined
    const rootDescription = rootSpan
      ? Sentry.spanToJSON(rootSpan).description
      : undefined
    const baseTxName = rootDescription ?? routeTransactionPath ?? 'unknown'
    if (originalTxNameRef.current === undefined) {
      originalTxNameRef.current = rootDescription ?? ''
    }

    const fullTxName = `${baseTxName}/${panelName}`

    const span = Sentry.startInactiveSpan({
      name: fullTxName,
      op: 'ui.panel',
      forceTransaction: true,
      attributes: { 'panel.type': panelName },
    })
    if (span) {
      previousActiveSpanRef.current = activeSpan ?? null
      Sentry.setActiveSpanInBrowser(span)
    }
    spanRef.current = span
    Sentry.getCurrentScope().setTransactionName(fullTxName)

    return () => {
      spanRef.current?.end()
      spanRef.current = null
      if (previousActiveSpanRef.current !== null) {
        const prev = previousActiveSpanRef.current
        previousActiveSpanRef.current = null
        Sentry.setActiveSpanInBrowser(prev)
      }
      if (originalTxNameRef.current !== undefined) {
        Sentry.getCurrentScope().setTransactionName(originalTxNameRef.current)
        originalTxNameRef.current = undefined
      }
    }
  }, [panelName, routeTransactionPath])
}
